/**
 * Pure, dependency-free helpers for the case-lifecycle ("Ukončiť prípad")
 * action on the inquiry detail view (#6317 C1 / #6019).
 *
 * Kept in their own module (no React / next-intl / axios imports) so tests can
 * import the REAL logic without pulling the component's provider graph into
 * bun's process-global `mock.module` cache (the codebase's testing convention —
 * see inquiryDetail.helpers.ts / InquiryDetailView.test.tsx).
 *
 * The state machine below is a verbatim mirror of the backend single source of
 * truth (`_CASE_TRANSITIONS` / `case_transition_allowed` in
 * `server/types/professional_match_types.py`). The frontend NEVER invents a
 * transition the backend would reject — it only renders the buttons for the
 * transitions the backend would accept, and the backend re-validates every
 * request server-side (authorization is resolved from the auth user, never
 * trusted from the body).
 */

import type { CaseStatus } from '@/types/professionals-types';
import type { InquiryViewerSide } from './inquiryDetail.helpers';

/** The acting party of a case transition — same two sides as the viewer. */
export type CaseActorRole = InquiryViewerSide; // 'client' | 'professional'

/**
 * Allowed case transitions keyed by the actor's side of the match.
 *
 * Verbatim mirror of the backend `_CASE_TRANSITIONS` table. Both parties may
 * advance `new → in_progress` and `in_progress → done`. The terminal EXITS are
 * per-party (advocate → `declined`, client → `withdrawn`) and are intentionally
 * NOT surfaced by this module — C1 is the forward path to `done` that unlocks
 * the review funnel; abandon/decline flows are a separate concern.
 *
 * `done` / `declined` / `withdrawn` are terminal — no transition leaves them.
 */
const CASE_TRANSITIONS: Record<
  CaseActorRole,
  Record<CaseStatus, readonly CaseStatus[]>
> = {
  client: {
    new: ['in_progress', 'withdrawn'],
    in_progress: ['done', 'withdrawn'],
    done: [],
    declined: [],
    withdrawn: [],
  },
  professional: {
    new: ['in_progress', 'declined'],
    in_progress: ['done', 'declined'],
    done: [],
    declined: [],
    withdrawn: [],
  },
} as const;

/**
 * Coerce a possibly-absent case status (older rows predate the column) into the
 * privacy/lifecycle-safe default `'new'`. Mirrors the backend default.
 */
export function normalizeCaseStatus(
  value: CaseStatus | null | undefined
): CaseStatus {
  return value ?? 'new';
}

/**
 * Return true iff `actorRole` may move the case `fromStatus → toStatus`.
 *
 * A no-op (`fromStatus === toStatus`) is NOT allowed — callers treat an
 * already-in-state request as nothing to do, not a valid transition. Mirrors
 * `case_transition_allowed` in the backend exactly.
 */
export function caseTransitionAllowed(args: {
  actorRole: CaseActorRole;
  fromStatus: CaseStatus;
  toStatus: CaseStatus;
}): boolean {
  return CASE_TRANSITIONS[args.actorRole][args.fromStatus].includes(
    args.toStatus
  );
}

/**
 * Whether this party may mark the case as `done` (the "Ukončiť prípad" action)
 * from its current status. Only `in_progress → done` qualifies for both sides.
 */
export function canMarkCaseDone(args: {
  actorRole: CaseActorRole;
  caseStatus: CaseStatus | null | undefined;
}): boolean {
  return caseTransitionAllowed({
    actorRole: args.actorRole,
    fromStatus: normalizeCaseStatus(args.caseStatus),
    toStatus: 'done',
  });
}

/**
 * Whether this party may advance the case to `in_progress` (the enabling step
 * toward `done`). Only `new → in_progress` qualifies for both sides.
 */
export function canStartCase(args: {
  actorRole: CaseActorRole;
  caseStatus: CaseStatus | null | undefined;
}): boolean {
  return caseTransitionAllowed({
    actorRole: args.actorRole,
    fromStatus: normalizeCaseStatus(args.caseStatus),
    toStatus: 'in_progress',
  });
}

/**
 * Which side's confirmation is still missing to reach `done` (#7007).
 *
 * Verbatim mirror of the backend `awaiting_case_done_confirmation_from`
 * (`server/types/professional_match_types.py`). Only meaningful while
 * `caseStatus` is `'in_progress'`; returns `null` once terminal (nothing left
 * to await) or when neither/both sides have confirmed (the "both" case
 * should be unreachable — `caseStatus` would already be `'done'` — but the
 * function stays total/defensive).
 */
export function awaitingCaseDoneConfirmationFrom(args: {
  caseStatus: CaseStatus | null | undefined;
  clientConfirmedAt: string | null | undefined;
  professionalConfirmedAt: string | null | undefined;
}): CaseActorRole | null {
  if (normalizeCaseStatus(args.caseStatus) !== 'in_progress') return null;
  if (args.clientConfirmedAt != null && args.professionalConfirmedAt == null) {
    return 'professional';
  }
  if (args.professionalConfirmedAt != null && args.clientConfirmedAt == null) {
    return 'client';
  }
  return null;
}

/**
 * Whether the anonymized review entry point is unlocked (#6317 / #6019).
 *
 * The review (#5088 / #5854) is gated on a real, COMPLETED interaction: the
 * entry point appears only once the case lifecycle reaches `done`. Mirrors the
 * backend `ProfessionalMatch.review_available` property.
 */
export function isReviewAvailable(
  caseStatus: CaseStatus | null | undefined
): boolean {
  return normalizeCaseStatus(caseStatus) === 'done';
}

// ==========================================================================
// CASE-TRANSITION OUTCOME — typed result of PATCH /matches/{id}/case-status
// ==========================================================================

/**
 * Discriminated-union result of a case-status transition request.
 *
 * Using a typed discriminant prevents callers from branching on error-message
 * wording (locale-dependent, brittle — same lesson as #6203). The 409 conflict
 * variants mirror the backend `detail.code` values:
 *   - `identity_reveal_required` → `IDENTITY_REVEAL_REQUIRED` (#6142): the
 *     advocate tried to take the case while the client is still anonymous.
 *   - `illegal_transition`       → `ILLEGAL_CASE_TRANSITION`: not permitted for
 *     this role/state (should be unreachable when the UI gates correctly, but
 *     surfaced for safety / concurrent edits).
 *   - `conflict`                 → `CASE_TRANSITION_CONFLICT`: a concurrent
 *     change moved the case off the expected state (also the generic-409 case).
 */
export type CaseTransitionOutcome =
  | {
      status: 'success';
      caseStatus: CaseStatus;
      caseDoneAt: string | null;
      reviewAvailable: boolean;
      /**
       * Mutual case-done confirmation gate (#7007). Present whenever the
       * backend returns them (always, additively) so callers can derive
       * "waiting for the other party" via `awaitingCaseDoneConfirmationFrom`
       * without a second outcome variant — a single confirmation is still a
       * `success`, just one where `caseStatus` is still `'in_progress'`.
       */
      caseDoneConfirmedClientAt: string | null;
      caseDoneConfirmedProfessionalAt: string | null;
    }
  | { status: 'identity_reveal_required' }
  | { status: 'illegal_transition' }
  | { status: 'conflict' }
  | { status: 'error' };

const CASE_STATUS_VALUES: readonly CaseStatus[] = [
  'new',
  'in_progress',
  'done',
  'declined',
  'withdrawn',
];

function isCaseStatus(value: unknown): value is CaseStatus {
  return (
    typeof value === 'string' &&
    (CASE_STATUS_VALUES as readonly string[]).includes(value)
  );
}

/**
 * Extract the backend `detail.code` from a FastAPI error body
 * (`{ detail: { code, ... } }`), tolerating string/other shapes.
 */
function extractConflictCode(body: unknown): string | undefined {
  if (body === null || typeof body !== 'object') return undefined;
  const detail = (body as Record<string, unknown>)['detail'];
  if (detail === null || typeof detail !== 'object') return undefined;
  const code = (detail as Record<string, unknown>)['code'];
  return typeof code === 'string' ? code : undefined;
}

/**
 * Map the (httpStatus, body) of a case-status PATCH to a typed outcome.
 *
 * @param httpStatus  200 on success; the rejected HTTP status on failure;
 *                    `undefined` for a network-level failure with no response.
 * @param body        On success: the `ApiRespV2` envelope (`{ status, data }`)
 *                    whose `data` is a `CaseStatusTransitionResponse`. On
 *                    failure: the raw error body (`err.response?.data`).
 */
export function classifyCaseTransitionOutcome(
  httpStatus: number | undefined,
  body: unknown
): CaseTransitionOutcome {
  if (httpStatus === 200) {
    const data =
      body !== null && typeof body === 'object'
        ? (body as Record<string, unknown>)['data']
        : undefined;
    if (data !== null && typeof data === 'object') {
      const d = data as Record<string, unknown>;
      if (isCaseStatus(d['case_status'])) {
        return {
          status: 'success',
          caseStatus: d['case_status'],
          caseDoneAt:
            typeof d['case_done_at'] === 'string' ? d['case_done_at'] : null,
          reviewAvailable: d['review_available'] === true,
          caseDoneConfirmedClientAt:
            typeof d['case_done_confirmed_client_at'] === 'string'
              ? d['case_done_confirmed_client_at']
              : null,
          caseDoneConfirmedProfessionalAt:
            typeof d['case_done_confirmed_professional_at'] === 'string'
              ? d['case_done_confirmed_professional_at']
              : null,
        };
      }
    }
    return { status: 'error' };
  }

  if (httpStatus === 409) {
    switch (extractConflictCode(body)) {
      case 'IDENTITY_REVEAL_REQUIRED':
        return { status: 'identity_reveal_required' };
      case 'ILLEGAL_CASE_TRANSITION':
        return { status: 'illegal_transition' };
      case 'CASE_TRANSITION_CONFLICT':
      default:
        return { status: 'conflict' };
    }
  }

  return { status: 'error' };
}
